import { HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AtlasLanguageService } from '@galaxy/atlas/core';
import { map } from 'rxjs/operators';
import {
  CRMOpportunityApiService,
  CreateCrmObjectRequestInterface,
  CreateCrmObjectResponseInterface,
  DeleteCrmObjectRequestInterface,
  GetMultiCrmObjectRequestInterface,
  GetMultiCrmObjectResponseInterface,
  ListCrmObjectsRequestInterface,
  ListCrmObjectsResponseInterface,
  ListFieldOptionsRequestInterface,
  ListFiltersRequestInterface,
  ListFiltersResponseInterface,
  UpdateCrmObjectRequestInterface,
  ValidateCrmObjectResponseInterface,
  GetOpportunityValueSummaryRequestInterface,
  GetOpportunityValueSummaryResponseInterface,
  ValidateCrmObjectRequestInterface,
  BulkDeleteCrmObjectsRequestInterface,
} from '@vendasta/crm';
import { Observable, of } from 'rxjs';
import { AbstractCrmObjectService } from './_abstract.service';
import { listOpportunities$, getMultiOpportunity$ } from '@vendasta/protos/crm/v1/crmopportunity-service';
import {
  toLegacyGetMultiResponse,
  toLegacyListResponse,
  toProtoGetMultiRequest,
  toProtoListRequest,
} from '@galaxy/crm/services/adapter-service';

@Injectable({ providedIn: 'root' })
export class OpportunityService implements AbstractCrmObjectService {
  private readonly languageService = inject(AtlasLanguageService);
  private locale = toSignal(this.languageService.language$);

  constructor(private readonly service: CRMOpportunityApiService) {}

  createCrmObject(request: CreateCrmObjectRequestInterface): Observable<CreateCrmObjectResponseInterface> {
    return this.service.createOpportunity(request);
  }

  deleteCrmObject(request: DeleteCrmObjectRequestInterface): Observable<HttpResponse<null>> {
    return this.service.deleteOpportunity(request);
  }

  validateCrmObject(request: ValidateCrmObjectRequestInterface): Observable<ValidateCrmObjectResponseInterface> {
    return this.service.validateOpportunity(request);
  }
  updateCrmObject(request: UpdateCrmObjectRequestInterface): Observable<HttpResponse<null>> {
    return this.service.updateOpportunity(request);
  }

  listObjects(request: ListCrmObjectsRequestInterface): Observable<ListCrmObjectsResponseInterface> {
    return listOpportunities$(toProtoListRequest(request)).pipe(map(toLegacyListResponse));
  }

  listObjectsFilters(request: ListFiltersRequestInterface): Observable<ListFiltersResponseInterface> {
    return this.service.listOpportunitiesFilters({ ...request, locale: this.locale() });
  }

  getMultiObject(request: GetMultiCrmObjectRequestInterface): Observable<GetMultiCrmObjectResponseInterface> {
    return getMultiOpportunity$(toProtoGetMultiRequest(request)).pipe(map(toLegacyGetMultiResponse));
  }

  listFieldOptions(request: ListFieldOptionsRequestInterface): Observable<string[]> {
    return this.service.listOpportunityFieldOptions(request).pipe(map((response) => response?.options || []));
  }

  getValueSummary(
    request: GetOpportunityValueSummaryRequestInterface,
  ): Observable<GetOpportunityValueSummaryResponseInterface> {
    return this.service.getOpportunityValueSummary(request);
  }

  bulkDelete(_request: BulkDeleteCrmObjectsRequestInterface): Observable<HttpResponse<null>> {
    // Not implemented in Opportunities API
    return of(new HttpResponse<null>({ status: 501, statusText: 'Not Implemented' }));
  }
}
